import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PUBLIC_SITE_URL } from "@/lib/publicUrl";
import { Building2, Plus, ExternalLink, X, Copy, CheckCircle2, AlertTriangle, FileSpreadsheet, FileText, Trash2, RefreshCw } from "lucide-react";

const cents = (n) => `€ ${Number(n || 0).toFixed(2)}`;

export default function AgencyOffices() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [quote, setQuote] = useState(null);
    const [cancelTarget, setCancelTarget] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [r1, r2] = await Promise.all([
                api.get("/agency/offices"),
                api.get("/agency/offices/quote"),
            ]);
            setData(r1.data);
            setQuote(r2.data);
        } catch (e) {
            setData({ items: [], count: 0, total_monthly: 129, main_agency_price: 129 });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div data-testid="agency-offices-page">
            <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <span className="label-eyebrow text-gold">Centro Operativo</span>
                    <h2 className="font-serif text-3xl text-ink-50 mt-2">I miei uffici</h2>
                    <p className="text-ink-400 text-sm mt-2">Gestisci le tue sedi aggiuntive. Ogni sede riceve un codice univoco da consegnare al responsabile.</p>
                </div>
                <button
                    data-testid="add-office-btn"
                    onClick={() => setShowAdd(true)}
                    className="cta-gold px-5 py-2.5 text-sm flex items-center gap-2"
                >
                    <Plus size={14} /> Aggiungi ufficio · {cents(quote?.price_monthly)}/mese
                </button>
            </div>

            {loading ? (
                <div className="text-ink-400 text-sm">Caricamento...</div>
            ) : (
                <>
                    {/* Riepilogo */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
                        <StatCard label="Sedi attive" value={(data.items || []).filter((o) => o.status === "active").length + 1} sub="incluso sede principale" />
                        <StatCard label="Totale mensile" value={cents(data.total_monthly)} sub="tutte le sedi" isPrice />
                        <StatCard label="Prossima sede" value={cents(quote?.price_monthly)} sub={`sede n°${quote?.next_order}`} isPrice />
                    </div>

                    {/* Sede principale */}
                    <div className="border border-gold/40 bg-gold/5 p-5 mb-4">
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 border border-gold flex items-center justify-center"><Building2 size={14} className="text-gold" /></div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-gold">Sede principale</div>
                                    <div className="font-serif text-lg text-ink-50">La tua agenzia</div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="font-serif text-xl text-price">€ 129,00<span className="text-xs text-ink-400 ml-1">/mese</span></div>
                            </div>
                        </div>
                    </div>

                    {/* Uffici aggiuntivi */}
                    {(data.items || []).length === 0 ? (
                        <div className="border border-white/10 border-dashed p-8 text-center text-sm text-ink-400">
                            Nessuna sede aggiuntiva. Aggiungi il tuo primo ufficio a {cents(quote?.price_monthly)}/mese.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {data.items.map((o) => <OfficeCard key={o.id} office={o} onCancel={() => setCancelTarget(o)} onReactivate={async () => { await api.post(`/agency/offices/${o.id}/reactivate`); load(); }} />)}
                        </div>
                    )}
                </>
            )}

            {showAdd && (
                <AddOfficeModal
                    quote={quote}
                    onClose={() => setShowAdd(false)}
                    onCreated={() => { setShowAdd(false); load(); }}
                />
            )}
            {cancelTarget && (
                <CancelOfficeModal
                    office={cancelTarget}
                    onClose={() => setCancelTarget(null)}
                    onCancelled={() => { setCancelTarget(null); load(); }}
                />
            )}
        </div>
    );
}

function StatCard({ label, value, sub, isPrice }) {
    return (
        <div className="border border-white/10 p-4">
            <div className="text-[10px] uppercase tracking-wider text-ink-400">{label}</div>
            <div className={`font-serif mt-1 ${isPrice ? "text-2xl text-price" : "text-2xl text-ink-50"}`}>{value}</div>
            <div className="text-[11px] text-ink-500 mt-0.5">{sub}</div>
        </div>
    );
}

function OfficeCard({ office, onCancel, onReactivate }) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(office.code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    const statusColor =
        office.status === "active" ? "text-green-400" :
        office.status === "pending" ? "text-amber-300" :
        office.status === "cancelled" ? "text-red-300" : "text-ink-400";
    return (
        <div data-testid={`office-card-${office.id}`} className="border border-white/10 hover:border-gold/40 p-5 transition-colors">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-9 h-9 border border-white/20 flex items-center justify-center shrink-0">
                        <Building2 size={14} className="text-gold" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="font-serif text-lg text-ink-50 truncate">{office.name}</div>
                            <span className={`text-[10px] uppercase tracking-wider ${statusColor}`}>{office.status}</span>
                            <span className="text-[10px] text-ink-400">sede n°{office.order}</span>
                        </div>
                        <div className="text-xs text-ink-400 mt-1">{office.city} · {office.address}</div>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                            <div className="text-[10px] uppercase tracking-wider text-ink-500">Codice ufficio</div>
                            <code className="px-2 py-1 border border-gold/40 bg-gold/5 text-gold font-mono text-xs">{office.code}</code>
                            <button onClick={copy} className="text-xs text-ink-400 hover:text-gold flex items-center gap-1">
                                {copied ? <><CheckCircle2 size={11} className="text-green-400" /> Copiato</> : <><Copy size={11} /> Copia</>}
                            </button>
                        </div>
                        {office.redeemed_by_email && (
                            <div className="text-[11px] text-ink-500 mt-2">Responsabile: <span className="text-ink-100">{office.redeemed_by_email}</span></div>
                        )}
                        {!office.redeemed_by_email && office.status === "active" && (
                            <div className="text-[11px] text-amber-300/80 mt-2">Consegna il codice al responsabile: potrà crearsi l&apos;account e accedere al CRM.</div>
                        )}
                        {office.status === "cancelled" && office.purge_at && (
                            <div className="text-[11px] text-red-300/80 mt-2">Dati conservati fino al {new Date(office.purge_at).toLocaleDateString("it-IT")}. Puoi riattivare l&apos;ufficio.</div>
                        )}
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <div className="font-serif text-xl text-price">{cents(office.price_monthly)}<span className="text-[11px] text-ink-400 ml-1">/mese</span></div>
                    <div className="mt-3 flex flex-wrap gap-2 justify-end">
                        <a
                            data-testid={`office-open-crm-${office.id}`}
                            href="/analytics-pro"
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-1.5 text-xs border border-gold/40 text-gold hover:bg-gold/10 flex items-center gap-1"
                        >
                            <ExternalLink size={11} /> Apri CRM
                        </a>
                        {office.status === "cancelled" ? (
                            <button
                                onClick={onReactivate}
                                className="px-3 py-1.5 text-xs border border-green-500/40 text-green-300 hover:bg-green-900/20 flex items-center gap-1"
                            >
                                <RefreshCw size={11} /> Riattiva
                            </button>
                        ) : (
                            <button
                                data-testid={`office-cancel-${office.id}`}
                                onClick={onCancel}
                                className="px-3 py-1.5 text-xs border border-red-500/40 text-red-300 hover:bg-red-900/20 flex items-center gap-1"
                            >
                                <Trash2 size={11} /> Disdici abbonamento
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function AddOfficeModal({ quote, onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", city: "", address: "" });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const submit = async () => {
        if (!form.name.trim() || !form.city.trim() || !form.address.trim()) {
            setError("Compila tutti i campi");
            return;
        }
        setSaving(true); setError("");
        try {
            const r = await api.post("/agency/offices/checkout", { ...form, origin_url: PUBLIC_SITE_URL });
            // redirect al checkout Stripe
            window.location.href = r.data.url;
        } catch (e) {
            setError(e.response?.data?.detail || "Errore creazione ufficio");
            setSaving(false);
        }
    };
    const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2 text-ink-50 focus:border-gold outline-none text-sm";
    return (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-6" onClick={onClose}>
            <div className="bg-ink-950 border border-gold/40 max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <span className="label-eyebrow text-gold">Nuova sede</span>
                        <h3 className="font-serif text-2xl text-ink-50 mt-2">Aggiungi ufficio</h3>
                    </div>
                    <button onClick={onClose} className="text-ink-400 hover:text-gold"><X size={16} /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs text-ink-400 uppercase tracking-wider">Nome sede</label>
                        <input data-testid="office-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Sede Milano Centro" className={inputCls} />
                    </div>
                    <div>
                        <label className="text-xs text-ink-400 uppercase tracking-wider">Città</label>
                        <input data-testid="office-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                        <label className="text-xs text-ink-400 uppercase tracking-wider">Indirizzo</label>
                        <input data-testid="office-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} />
                    </div>
                </div>
                <div className="border border-gold/30 bg-gold/5 p-4 my-6">
                    <div className="text-[10px] uppercase tracking-wider text-ink-400">Prezzo mensile per questa sede</div>
                    <div className="font-serif text-2xl text-price mt-1">{cents(quote?.price_monthly)}</div>
                    <div className="text-[11px] text-ink-400 mt-1">Sede n°{quote?.next_order} · si aggiunge al totale mensile</div>
                </div>
                {error && <div className="mb-4 p-3 border border-red-500/40 bg-red-900/20 text-red-200 text-xs flex items-center gap-2"><AlertTriangle size={12} /> {error}</div>}
                <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 px-5 py-2.5 text-sm border border-white/15 text-ink-100 hover:border-white/30">Annulla</button>
                    <button data-testid="office-submit" onClick={submit} disabled={saving} className="flex-1 cta-gold px-5 py-2.5 text-sm disabled:opacity-50">
                        {saving ? "Attendere..." : "Conferma e paga"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function CancelOfficeModal({ office, onClose, onCancelled }) {
    const [downloaded, setDownloaded] = useState({ xlsx: false, pdf: false });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const download = (fmt) => {
        const url = `/api${`/agency/offices/${office.id}/export.${fmt}`}`;
        // Uses the app's session cookie
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.rel = "noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setDownloaded((d) => ({ ...d, [fmt]: true }));
    };
    const confirm = async (skip = false) => {
        setSaving(true); setError("");
        try {
            await api.post(`/agency/offices/${office.id}/cancel`, {
                data_exported: downloaded.xlsx || downloaded.pdf,
                skip_export: skip,
                reason: "cancellata dal titolare",
            });
            onCancelled();
        } catch (e) {
            setError(e.response?.data?.detail || "Errore");
            setSaving(false);
        }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-6" onClick={onClose}>
            <div className="bg-ink-950 border border-red-500/40 max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-6">
                    <div className="flex items-center gap-2">
                        <AlertTriangle size={18} className="text-red-300" />
                        <div>
                            <span className="label-eyebrow text-red-300">Attenzione</span>
                            <h3 className="font-serif text-xl text-ink-50 mt-2">Disdici abbonamento ufficio</h3>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-ink-400 hover:text-gold"><X size={16} /></button>
                </div>
                <div className="text-sm text-ink-100 mb-4">
                    Stai per disdire l&apos;abbonamento di <b className="text-gold">{office.name}</b>. L&apos;accesso al CRM per questo ufficio verrà disattivato.
                </div>
                <div className="text-xs text-ink-400 mb-6">
                    I dati resteranno salvati per <b className="text-ink-100">30 giorni</b>. Potrai riattivare l&apos;ufficio prima della scadenza. Ti consigliamo di scaricare i dati ora.
                </div>

                <div className="border border-gold/30 bg-gold/5 p-4 mb-6">
                    <div className="text-[10px] uppercase tracking-wider text-gold mb-3">Scarica i dati prima di procedere</div>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <button data-testid="dl-xlsx" onClick={() => download("xlsx")} className="flex-1 px-4 py-2 text-xs border border-gold/40 text-gold hover:bg-gold/10 flex items-center justify-center gap-2">
                            {downloaded.xlsx ? <CheckCircle2 size={12} className="text-green-400" /> : <FileSpreadsheet size={12} />} Excel (.xlsx)
                        </button>
                        <button data-testid="dl-pdf" onClick={() => download("pdf")} className="flex-1 px-4 py-2 text-xs border border-gold/40 text-gold hover:bg-gold/10 flex items-center justify-center gap-2">
                            {downloaded.pdf ? <CheckCircle2 size={12} className="text-green-400" /> : <FileText size={12} />} PDF
                        </button>
                    </div>
                </div>

                {error && <div className="mb-4 p-3 border border-red-500/40 bg-red-900/20 text-red-200 text-xs">{error}</div>}

                <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 px-5 py-2.5 text-sm border border-white/15 text-ink-100 hover:border-white/30">Annulla</button>
                    {(downloaded.xlsx || downloaded.pdf) ? (
                        <button data-testid="confirm-cancel" onClick={() => confirm(false)} disabled={saving} className="flex-1 px-5 py-2.5 text-sm bg-red-900/40 border border-red-500/60 text-red-100 hover:bg-red-900/60 disabled:opacity-50">
                            {saving ? "Attendere..." : "Ho scaricato · Disdici"}
                        </button>
                    ) : (
                        <button data-testid="skip-cancel" onClick={() => confirm(true)} disabled={saving} className="flex-1 px-5 py-2.5 text-sm border border-red-500/40 text-red-300 hover:bg-red-900/20 disabled:opacity-50">
                            {saving ? "Attendere..." : "Salta export · Disdici"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
